// The Map view on Vehicle Allocation: a list of cars ranked by crew spread,
// beside a map of where the men on each car actually live.
//
// It reads no new endpoint. Every figure here comes from reads the allocation
// page already makes, which is why the parity set is untouched: seats from
// v_allocation_current, coordinates from v_driver_address_health, the bench
// from v_allocation_bench, and the manager name from the driver manager board.
//
// Two rules from the working agreement shape what is on screen:
//   - A missing figure is missing, never zero. A crew of one has no spread, and
//     says so.
//   - A driver who cannot be drawn is named, not quietly dropped. The count at
//     the top and the list at the bottom exist so nobody reads this map as the
//     whole fleet when it is not.
import { useMemo, useState } from 'react'
import { AlertTriangle, MapPin, Users } from 'lucide-react'
import { Switch } from '../../lib/shadcn/switch'
import { Label } from '../../lib/shadcn/label'
import { Chip } from '../interviews/Chip'
import { cn } from '../../lib/shadcn/utils'
import { AllocationMap } from './AllocationMap'
import {
  buildAllocationMapModel,
  formatKm,
  medianSpreadKm,
  type CarPlacement,
} from '../../utils/allocationMap'
import { humanize, type AllocationSeat } from '../../utils/allocation'
import type { AddressHealthRow } from '../../utils/addressHealth'
import type { DriverBoardRow } from '../../utils/managers'

type Props = {
  seats: AllocationSeat[]
  addresses: AddressHealthRow[]
  bench: { driver_id: string; driver_name: string | null }[]
  driverBoard: DriverBoardRow[]
  loading: boolean
}

// Above this, a crew is worth a conversation. Not a rule anyone has signed: it
// is a reading aid, and it is stated on screen as one so nobody mistakes it for
// a policy. A real threshold belongs in a table with a date and an author, the
// way maintenance_charge_rules does, on the day someone decides one.
const WIDE_CREW_KM = 25

function spreadTone(km: number | null): 'neutral' | 'amber' | 'red' {
  if (km === null) return 'neutral'
  if (km >= WIDE_CREW_KM * 1.6) return 'red'
  if (km >= WIDE_CREW_KM) return 'amber'
  return 'neutral'
}

export function AllocationMapView({ seats, addresses, bench, driverBoard, loading }: Props) {
  const [selected, setSelected] = useState<string | null>(null)
  const [showBench, setShowBench] = useState(true)

  const model = useMemo(
    () => buildAllocationMapModel({ seats, addresses, bench, driverBoard }),
    [seats, addresses, bench, driverBoard],
  )

  const mappedSeated = model.cars.reduce((n, c) => n + c.drivers.length, 0)
  const median = medianSpreadKm(model.cars)
  const widest = model.cars.find((c) => c.spread_km !== null) ?? null

  const selectedCar: CarPlacement | null = selected
    ? (model.cars.find((c) => c.vehicle_id === selected) ?? null)
    : null

  return (
    <div className="space-y-4">
      {/* What is on the map, and what is not. */}
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          label="Seated drivers on the map"
          value={loading ? '...' : String(mappedSeated)}
          note={`across ${model.cars.length} car${model.cars.length === 1 ? '' : 's'}`}
        />
        <Tile
          label="Bench drivers on the map"
          value={loading ? '...' : String(model.bench.length)}
          note="eligible, holding no seat this run"
        />
        <Tile
          label="Median crew spread"
          value={loading ? '...' : formatKm(median)}
          note="straight line, not driving distance"
        />
        <Tile
          label="Widest crew"
          value={loading ? '...' : widest ? formatKm(widest.spread_km) : 'Not known'}
          note={widest ? widest.plate : 'no car has two mapped drivers'}
        />
      </div>

      {model.unmapped.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">
              {model.unmapped.length} driver{model.unmapped.length === 1 ? ' is' : 's are'} not on
              this map
            </p>
            <p className="text-xs">
              Every spread below is computed on the drivers that could be placed. Fix the address on
              Driver Address Health and the figure changes.
            </p>
            <ul className="mt-1.5 space-y-0.5">
              {model.unmapped.map((u) => (
                <li key={u.driver_id} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                  <span className="font-medium">{u.driver_name}</span>
                  {u.plate ? <span className="font-mono">{u.plate}</span> : <span>Bench</span>}
                  <span className="opacity-80">{u.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        {/* Cars, widest crew first. */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <Users className="h-4 w-4 text-muted-foreground" />
              Crews, widest first
            </h2>
            {selectedCar ? (
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted"
              >
                Clear
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5">
            <Switch id="map-show-bench" checked={showBench} onCheckedChange={setShowBench} />
            <Label htmlFor="map-show-bench" className="cursor-pointer text-xs font-normal">
              Show bench drivers
            </Label>
          </div>

          <div className="max-h-[26rem] space-y-1.5 overflow-y-auto pr-1 lg:max-h-[34rem]">
            {model.cars.length === 0 ? (
              <p className="text-sm text-muted-foreground">No vehicles in the current run.</p>
            ) : (
              model.cars.map((c) => {
                const isSelected = c.vehicle_id === selected
                return (
                  <button
                    key={c.vehicle_id}
                    type="button"
                    onClick={() => setSelected(isSelected ? null : c.vehicle_id)}
                    className={cn(
                      'w-full rounded-lg border p-2.5 text-left transition-colors',
                      isSelected
                        ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))]'
                        : 'border-border bg-background hover:bg-muted',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate font-mono text-sm font-semibold text-foreground">
                        {c.plate}
                      </span>
                      <span
                        className={cn(
                          'shrink-0 text-sm font-semibold tabular-nums',
                          spreadTone(c.spread_km) === 'red'
                            ? 'text-rose-600 dark:text-rose-400'
                            : spreadTone(c.spread_km) === 'amber'
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-foreground',
                        )}
                      >
                        {formatKm(c.spread_km)}
                      </span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {[c.make, c.model].filter(Boolean).join(' ') || 'Unknown model'}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                      <Chip tone="neutral">
                        {c.seats_filled} of {c.seats_total} seats
                      </Chip>
                      <Chip tone="neutral">{humanize(c.operational_state)}</Chip>
                      {c.unmapped.length > 0 && (
                        <Chip tone="amber">{c.unmapped.length} not placed</Chip>
                      )}
                    </div>
                    {isSelected && c.drivers.length > 0 && (
                      <ul className="mt-2 space-y-1 border-t border-[hsl(var(--brand))]/30 pt-2">
                        {c.drivers.map((d) => (
                          <li
                            key={d.driver_id}
                            className="flex items-baseline gap-1.5 text-xs text-[hsl(var(--brand-soft-foreground))]"
                          >
                            <span className="font-semibold tabular-nums">{d.shift_index}</span>
                            <span className="truncate font-medium">{d.driver_name}</span>
                            <span className="ml-auto truncate opacity-80">
                              {d.suburb_or_city ?? 'Place not recorded'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </button>
                )
              })
            )}
          </div>
        </div>

        {/* The map itself. */}
        <div className="flex min-h-[26rem] flex-col gap-2 lg:min-h-[38rem]">
          <div className="min-h-0 flex-1">
            <AllocationMap
              cars={model.cars}
              bench={model.bench}
              showBench={showBench}
              selectedVehicleId={selected}
              onSelectVehicle={setSelected}
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="fleetly-pin" style={{ width: 14, height: 14, fontSize: 8 }}>
                1
              </span>
              Seated driver, number is the seat
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="fleetly-pin fleetly-pin--selected"
                style={{ width: 14, height: 14, fontSize: 8 }}
              >
                1
              </span>
              Crew of the selected car
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="fleetly-pin fleetly-pin--planned"
                style={{ width: 14, height: 14, fontSize: 8 }}
              >
                1
              </span>
              Planned seat, Uber has not confirmed it
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="fleetly-pin fleetly-pin--bench"
                style={{ width: 14, height: 14, fontSize: 8 }}
              >
                B
              </span>
              Bench, no seat this run
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3 w-3" />
              A pin is where a driver lives, not where he is now.
            </span>
          </div>
          <p className="text-[11px] leading-snug text-muted-foreground">
            Spread is the greatest straight line distance between two men sharing a car, which is
            the handover they make twice a day. It is not driving distance and no route is
            calculated. The {WIDE_CREW_KM} km colour change is a reading aid chosen for this screen,
            not a policy anybody has agreed.
          </p>
        </div>
      </div>
    </div>
  )
}

function Tile({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tabular-nums text-foreground">{value}</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{note}</p>
    </div>
  )
}
